// src/components/AnimatedLayout.j
import React, { useState, useEffect, useRef, useMemo } from 'react';
import Layout from './Layout';
import IndexPage from '../pages/index.js';
import ResearchPage from '../pages/research.js';
import DesignPage from '../pages/design.js';
import AboutPage from '../pages/about.js';

const AnimatedLayout = ({ children, location }) => {
  const [activatedSidebar, setActivatedSidebar] = useState('');
  const [wasSelectedSidebar, setWasSelectedSidebar] = useState('');
  const [isPageOpen, setIsPageOpen] = useState(false);
  const [animationStage, setAnimationStage] = useState('visible');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isReturning, setIsReturning] = useState(false);
  const prevPathname = useRef('');
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    const handlePopState = () => {
      const currentPath = location.pathname;
      const sidebarId = currentPath.replace(/^\//, '').replace(/\/$/, '');
      const isOpen = currentPath !== '/';

      setActivatedSidebar(sidebarId);
      setWasSelectedSidebar(sidebarId);
      setIsPageOpen(isOpen);
      setAnimationStage(isOpen ? 'opened' : 'visible');
      setIsTransitioning(false);
      setIsReturning(false);
      setIsCollapsed(false);

      prevPathname.current = currentPath;
    };

    window.addEventListener('popstate', handlePopState);

    // Call handlePopState initially to handle the case of refreshing a specific page
    handlePopState();

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [location.pathname]);

  useEffect(() => {
    const currentPath = location.pathname;
    const isOpen = currentPath !== '/';

    if (prevPathname.current !== currentPath) {
      setIsPageOpen(isOpen);
      setIsTransitioning(false);

      if (isOpen) {
        setWasSelectedSidebar(activatedSidebar);
        setAnimationStage('opened');
      } else {
        setWasSelectedSidebar('');
        setIsReturning(false);
        setAnimationStage('visible');
      }

      setActivatedSidebar('');
      setIsCollapsed(false);
      prevPathname.current = currentPath;
    }
  }, [location.pathname, isTransitioning, isReturning, activatedSidebar]);

  useEffect(() => {
    if (animationStage === 'toRight') {
      setIsCollapsed(true);
    } else if (animationStage === 'opened') {
      setIsCollapsed(false);
    }
  }, [animationStage]);

  const currentPage = isCollapsed ? activatedSidebar : wasSelectedSidebar;

  const pageContent = useMemo(() => {
    switch (currentPage) {
      case 'research':
        return <ResearchPage />;
      case 'design':
        return <DesignPage />;
      case 'about':
        return <AboutPage />;
      default:
        return null;
    }
  }, [currentPage]);

  const homeContent = <IndexPage />;

  return (
    <Layout
      activatedSidebar={activatedSidebar}
      setActivatedSidebar={setActivatedSidebar}
      wasSelectedSidebar={wasSelectedSidebar}
      isPageOpen={isPageOpen}
      prevLocation={prevPathname.current}
      animationStage={animationStage}
      setAnimationStage={setAnimationStage}
      isTransitioning={isTransitioning}
      setIsTransitioning={setIsTransitioning}
      isReturning={isReturning}
      setIsReturning={setIsReturning}
      homeContent={homeContent}
      setWasSelectedSidebar={setWasSelectedSidebar}
    >
      {pageContent}
    </Layout>
  );
};

export default AnimatedLayout;
